/**
 * Waiting for a host's decision, told by signaling rather than asked of the
 * backend.
 *
 * A joiner who has to wait is handed a lobby channel at the door
 * (`LobbyWait`): a token, and a `lobby:<request_id>` topic on signaling's
 * socket. The join reply says where the request stands, and `lobby:status` is
 * pushed when that changes — admitted with the room credentials, denied, full,
 * or gone — in the shape `/guest/lobby-status` answers with. That poll, every
 * 2 seconds through the backend and on to signaling, is what this replaces.
 *
 * `unavailable` is the way back to it: a socket that will not open, or a join
 * that is refused or never answered. The caller then polls as before, so
 * nobody is left waiting on a channel that is not there.
 */
import { Socket } from 'phoenix'
import type { LobbyStatusResult, LobbyWait } from './meetings.api'

/** A decision, as the channel pushes it. `gone`: the request is no longer there. */
export type LobbyDecision = (Exclude<LobbyStatusResult, { status: 'pending' }> | { status: 'gone' }) & {
  /** A Screen Share page's request, and the id it was let in as. */
  mode?: 'share'
  shareId?: string
}

export type LobbyWatch = {
  /**
   * Stop listening and leave the request where it is — a remount, a page
   * going away. Nobody coming back for it, signaling drops it within minutes.
   */
  stop: () => void
  /** Leave the waiting room: signaling withdraws the request, and the host's list drops it at once. */
  withdraw: () => void
}

/** A join that has not been answered by now is not going to be. */
const JOIN_TIMEOUT_MS = 10_000

/** Socket failures in a row before the channel is given up for the poll. */
const MAX_SOCKET_ERRORS = 3

/** How long a withdrawal waits for its `phx_leave` to be answered before closing anyway. */
const LEAVE_TIMEOUT_MS = 2_000

export function watchLobby(
  wait: LobbyWait,
  on: { decided: (decision: LobbyDecision) => void; unavailable: () => void },
): LobbyWatch {
  const socket = new Socket(wait.signalingWsUrl, {
    // In the `Sec-WebSocket-Protocol` header, never the URL (phoenix-auth-token.ts).
    authToken: wait.token,
    reconnectAfterMs: (tries: number) => Math.min(1000 * 2 ** Math.max(tries - 1, 0), 10_000),
    heartbeatIntervalMs: 30_000,
  })
  const channel = socket.channel(wait.topic, {})
  let over = false
  let socketErrors = 0

  const close = (leave: boolean) => {
    if (!leave) {
      socket.disconnect()
      return
    }
    // `phx_leave` is what tells signaling this person left on purpose; a
    // closed socket alone reads as a dropped connection, which it waits out.
    const done = () => socket.disconnect()
    const fallback = setTimeout(done, LEAVE_TIMEOUT_MS)
    channel
      .leave(LEAVE_TIMEOUT_MS)
      .receive('ok', () => {
        clearTimeout(fallback)
        done()
      })
  }

  const giveUp = () => {
    if (over) return
    over = true
    close(false)
    on.unavailable()
  }

  const answer = (payload: unknown) => {
    if (over) return
    const decision = asDecision(payload, wait)
    if (decision === 'pending') return
    if (decision === null) return giveUp()
    over = true
    close(false)
    on.decided(decision)
  }

  channel.on('lobby:status', answer)
  channel
    .join(JOIN_TIMEOUT_MS)
    .receive('ok', (reply: unknown) => {
      socketErrors = 0
      answer(reply)
    })
    // Refused — a token for another request, or one that has expired — or
    // never answered: the poll still works without either.
    .receive('error', giveUp)
    .receive('timeout', giveUp)

  socket.onOpen(() => {
    socketErrors = 0
  })
  socket.onError(() => {
    socketErrors += 1
    if (socketErrors >= MAX_SOCKET_ERRORS) giveUp()
  })
  socket.connect()

  return {
    stop: () => {
      if (over) return
      over = true
      close(false)
    },
    withdraw: () => {
      if (over) return
      over = true
      close(true)
    },
  }
}

/**
 * What a join reply or `lobby:status` push says. `'pending'` to keep waiting,
 * `null` for something that cannot be acted on — the caller falls back to the
 * poll, which can.
 */
export function asDecision(payload: unknown, wait: LobbyWait): LobbyDecision | 'pending' | null {
  const p = (payload ?? {}) as {
    status?: unknown
    roomCredentials?: Record<string, unknown> | null
    mode?: unknown
    shareId?: unknown
  }
  const tag = {
    ...(p.mode === 'share' ? { mode: 'share' as const } : {}),
    ...(typeof p.shareId === 'string' ? { shareId: p.shareId } : {}),
  }

  switch (p.status) {
    case 'pending':
      return 'pending'
    case 'admitted':
      if (!p.roomCredentials || typeof p.roomCredentials.roomToken !== 'string') return null
      return {
        status: 'admitted',
        // The socket address signaling would have said, had the knock that
        // queued this request carried it: the one this channel is open on.
        roomCredentials: {
          ...p.roomCredentials,
          signalingWsUrl: p.roomCredentials.signalingWsUrl || wait.signalingWsUrl,
        },
        ...tag,
      }
    case 'denied':
    case 'full':
    case 'gone':
      return { status: p.status, ...tag }
    default:
      return null
  }
}
